<script setup lang="ts">
const props = withDefaults(defineProps<{
  modelValue?: boolean
  color?: string
  timeout?: number
  location?: 'top' | 'bottom'
  multiLine?: boolean
  vertical?: boolean
}>(), {
  timeout: 5000,
  location: 'bottom',
})

const emit = defineEmits<{
  'update:modelValue': [val: boolean]
}>()

let timer: ReturnType<typeof setTimeout>

watch(() => props.modelValue, (v) => {
  if (v && props.timeout > 0) {
    clearTimeout(timer)
    timer = setTimeout(() => emit('update:modelValue', false), props.timeout)
  }
}, { immediate: true })

onUnmounted(() => clearTimeout(timer))

const colorMap: Record<string, string> = {
  success: 'var(--m-success)',
  error: 'var(--m-error)',
  warning: 'var(--m-warning)',
  info: 'var(--m-info)',
  primary: 'var(--m-primary)',
}
</script>

<template>
  <Teleport to="body">
    <Transition name="m-snackbar">
      <div
        v-if="modelValue"
        class="m-snackbar"
        :class="[`m-snackbar--${location}`, { 'm-snackbar--multi-line': multiLine }]"
        :style="{ backgroundColor: colorMap[color || ''] || color || '#333' }"
        role="status"
      >
        <div class="m-snackbar__content">
          <slot />
        </div>
        <div class="m-snackbar__actions">
          <slot name="actions">
            <button class="m-snackbar__close" @click="emit('update:modelValue', false)">
              <MIcon icon="close" size="small" color="white" />
            </button>
          </slot>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.m-snackbar {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  z-index: var(--m-z-snackbar);
  display: flex;
  align-items: center;
  gap: var(--m-space-2);
  padding: var(--m-space-3) var(--m-space-4);
  border-radius: var(--m-radius);
  color: #fff;
  font-size: var(--m-font-size-sm);
  min-width: 300px;
  max-width: 600px;
  box-shadow: var(--m-shadow-lg);
}

.m-snackbar--bottom { bottom: var(--m-space-6); }
.m-snackbar--top { top: var(--m-space-6); }

.m-snackbar__content { flex: 1; }

.m-snackbar__actions { display: flex; gap: var(--m-space-1); flex-shrink: 0; }

.m-snackbar__close {
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--m-space-1);
  color: inherit;
  display: flex;
}

.m-snackbar-enter-active,
.m-snackbar-leave-active {
  transition: opacity var(--m-transition), transform var(--m-transition);
}
.m-snackbar-enter-from,
.m-snackbar-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}
.m-snackbar--top.m-snackbar-enter-from,
.m-snackbar--top.m-snackbar-leave-to {
  transform: translateX(-50%) translateY(-20px);
}
</style>
